import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { NumberInput } from '../components/NumberInput';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import api from '../utils/api';
import { formatCurrency } from '../utils/helpers';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useConfirm } from '../context/ConfirmContext';
import { UnitsManager } from '../components/UnitsManager';

export const ItemsPage = () => {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  const emptyItem = {
    name: '',
    category: '',
    unit: '',
    standard_price_chf: 0,
    mwst_rate: 8.1,
    active: true
  };

  useEffect(() => {
    loadItems();
    loadUnits();
  }, []);

  const loadItems = async () => {
    try {
      const response = await api.getItems(false);
      setItems(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento articoli');
    } finally {
      setLoading(false);
    }
  };

  const loadUnits = async () => {
    try {
      // For the unit Select inside the item dialog we only want active units
      const response = await api.getUnits(true);
      setUnits(response.data);
    } catch (error) {
      console.error('Failed to load units:', error);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (currentItem?.standard_price_chf == null || Number.isNaN(currentItem.standard_price_chf)) {
      toast.error('Inserire un prezzo standard valido');
      return;
    }
    try {
      if (currentItem.id) {
        await api.updateItem(currentItem.id, currentItem);
        toast.success('Articolo aggiornato');
      } else {
        await api.createItem(currentItem);
        toast.success('Articolo creato');
      }
      setDialogOpen(false);
      setCurrentItem(null);
      loadItems();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleDelete = async (itemId) => {
    const ok = await confirm({
      title: 'Elimina articolo',
      description: 'Stai per eliminare definitivamente questo articolo. Articoli usati in bollettini o fatture non possono essere eliminati.',
      confirmText: 'Elimina',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.deleteItem(itemId);
      toast.success('Articolo eliminato');
      setDialogOpen(false);
      setCurrentItem(null);
      loadItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const canEdit = user.role === 'ADMIN' || user.role === 'MANAGER';

  return (
    <div data-testid="items-page">
      <div className="mb-8">
        <h1 className="font-heading text-4xl font-bold text-slate-900 mb-2">Articoli e Servizi</h1>
        <p className="text-slate-600">Gestisci il listino articoli e le unità di misura</p>
      </div>

      <Tabs defaultValue="items" className="space-y-6" onValueChange={(v) => { if (v === 'items') loadUnits(); }}>
        <TabsList>
          <TabsTrigger value="items" data-testid="tab-items">Articoli</TabsTrigger>
          <TabsTrigger value="units" data-testid="tab-units">Unità di Misura</TabsTrigger>
        </TabsList>

        <TabsContent value="items">
          {/* Items List */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Tutti gli Articoli</CardTitle>
              {canEdit && (
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button onClick={() => setCurrentItem(emptyItem)} data-testid="create-item-btn">
                      <Plus size={18} className="mr-2" />
                      Nuovo Articolo
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{currentItem?.id ? 'Modifica Articolo' : 'Nuovo Articolo'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSave} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Nome *</Label>
                        <Input
                          id="name"
                          value={currentItem?.name || ''}
                          onChange={(e) => setCurrentItem({ ...currentItem, name: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="category">Categoria</Label>
                        <Input
                          id="category"
                          value={currentItem?.category || ''}
                          onChange={(e) => setCurrentItem({ ...currentItem, category: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="unit">Unità *</Label>
                        <Select
                          value={currentItem?.unit || ''}
                          onValueChange={(value) => setCurrentItem({ ...currentItem, unit: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleziona unità" />
                          </SelectTrigger>
                          <SelectContent>
                            {units.length === 0 ? (
                              <div className="p-2 text-sm text-slate-500">Nessuna unità configurata</div>
                            ) : (
                              units.map(unit => (
                                <SelectItem key={unit.id} value={unit.code}>
                                  {unit.name} ({unit.code})
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                        {units.length === 0 && (
                          <p className="text-xs text-amber-600">
                            Configura le unità di misura nel tab &quot;Unità di Misura&quot;
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="price">Prezzo Standard CHF *</Label>
                        <NumberInput
                          id="price"
                          step="0.01"
                          value={currentItem?.standard_price_chf ?? null}
                          onChange={(val) => setCurrentItem({ ...currentItem, standard_price_chf: val })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="mwst">Aliquota IVA % *</Label>
                        <Select
                          value={currentItem?.mwst_rate?.toString() || '8.1'}
                          onValueChange={(value) => setCurrentItem({ ...currentItem, mwst_rate: parseFloat(value) })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="8.1">8.1%</SelectItem>
                            <SelectItem value="2.6">2.6%</SelectItem>
                            <SelectItem value="0">0%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="item_status">Stato *</Label>
                        <Select
                          value={(currentItem?.active === false) ? 'suspended' : 'active'}
                          onValueChange={(value) => setCurrentItem({ ...currentItem, active: value === 'active' })}
                        >
                          <SelectTrigger data-testid="item-status-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">Attivo</SelectItem>
                            <SelectItem value="suspended">Sospeso</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500">Gli articoli sospesi non sono selezionabili nei bollettini</p>
                      </div>
                      <div className="flex justify-between items-center pt-2">
                        {currentItem?.id ? (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => handleDelete(currentItem.id)}
                            className="text-destructive border-destructive/30 hover:bg-destructive/10"
                            data-testid="dialog-delete-item-btn"
                          >
                            <Trash2 size={16} className="mr-2" />
                            Elimina
                          </Button>
                        ) : <span />}
                        <div className="flex gap-2">
                          <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                            Annulla
                          </Button>
                          <Button type="submit">Salva</Button>
                        </div>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </CardHeader>
            <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 bg-slate-100 animate-pulse rounded"></div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600">Nessun articolo trovato</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-secondary border-b">
                  <tr>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Nome</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Categoria</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Unità</th>
                    {user.role !== 'EMPLOYEE' && (
                      <>
                        <th className="text-right p-4 text-sm font-semibold text-slate-700">Prezzo CHF</th>
                        <th className="text-right p-4 text-sm font-semibold text-slate-700">IVA %</th>
                      </>
                    )}
                    <th className="text-center p-4 text-sm font-semibold text-slate-700">Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr
                      key={item.id}
                      className={`border-b hover:bg-secondary ${canEdit ? 'cursor-pointer' : ''}`}
                      onClick={canEdit ? () => { setCurrentItem(item); setDialogOpen(true); } : undefined}
                      data-testid={`item-row-${item.id}`}
                    >
                      <td className="p-4 font-medium text-slate-900">{item.name}</td>
                      <td className="p-4 text-slate-600">{item.category || '-'}</td>
                      <td className="p-4 text-slate-600">{item.unit}</td>
                      {user.role !== 'EMPLOYEE' && (
                        <>
                          <td className="p-4 text-right font-mono">
                            {item.standard_price_chf ? formatCurrency(item.standard_price_chf) : '-'}
                          </td>
                          <td className="p-4 text-right">{item.mwst_rate}%</td>
                        </>
                      )}
                      <td className="p-4 text-center">
                        {item.active ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                            Attivo
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                            Sospeso
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="units">
          <UnitsManager />
        </TabsContent>
      </Tabs>
    </div>
  );
};
